import {
  Box,
  Button,
  Dialog,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  FC,
  KeyboardEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { renderMarkdown } from '../../helpers/markdown'
import { useUser } from '../../hooks/user'
import {
  SupportChatMessage,
  SupportChatService,
} from '../../services/support-chat'
import { gymState } from '../../store/gym'
import { Fa } from '../Fa'
import {
  SupportChat as SupportChatState,
  useSupportChat,
} from './useSupportChat'

const PANEL_TITLE_ID = 'support-chat-title'

/**
 * "Help" entry in the header for signed-in members. Opens the support
 * assistant: a popover under the button, or a full-screen dialog on phones.
 */
const SupportChat: FC = () => {
  const { user } = useUser()
  return user ? <SupportChatEntry userId={user.userId} /> : null
}

const SupportChatEntry: FC<{ userId: number }> = ({ userId }) => {
  const { t } = useTranslation()
  const gym = useRecoilValue(gymState)
  const chat = useSupportChat({ userId, gymName: gym?.name })

  const [anchor, setAnchor] = useState<HTMLButtonElement>()
  const close = useCallback(() => setAnchor(undefined), [])

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Fa name="circle-question" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        data-testid="support-chat-trigger"
      >
        {t('Help')}
      </Button>
      <SupportChatPanel anchor={anchor} onClose={close} chat={chat} />
    </>
  )
}

const SupportChatPanel: FC<SupportChatPanelProps> = ({
  anchor,
  onClose,
  chat,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const open = !!anchor

  const { messages, sending, error, canRetry, send, retry, reset } = chat
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  const focusInput = useCallback(() => {
    // Wait for the open transition before focusing
    window.setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  useEffect(() => {
    if (open && !sending) {
      focusInput()
    }
  }, [open, sending, focusInput])

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' })
  }, [open, messages, sending, error])

  const handleSend = useCallback(() => {
    if (send(draft)) {
      setDraft('')
    }
  }, [draft, send])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const handleNewConversation = useCallback(() => {
    reset()
    setDraft('')
    focusInput()
  }, [reset, focusInput])

  const max = SupportChatService.MAX_MESSAGE_LENGTH
  const overLimit = draft.length > max
  const canSend = !sending && !!draft.trim() && !overLimit

  const content = (
    <Stack
      role="dialog"
      aria-labelledby={PANEL_TITLE_ID}
      sx={{
        blockSize: downSm ? '100%' : 560,
        maxBlockSize: downSm ? 'none' : 'calc(100vh - 120px)',
        inlineSize: downSm ? '100%' : 420,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        px={2}
        py={1.5}
        borderBottom={1}
        borderColor="divider"
      >
        <Typography id={PANEL_TITLE_ID} variant="h3" flexGrow={1}>
          {t('Help')}
        </Typography>
        <Tooltip title={t('New conversation')}>
          <IconButton
            size="small"
            onClick={handleNewConversation}
            aria-label={t('New conversation')}
          >
            <Fa name="comment-plus" size="sm" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('Close')}>
          <IconButton size="small" onClick={onClose} aria-label={t('Close')}>
            <Fa name="xmark" size="sm" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        flexGrow={1}
        overflow="auto"
        px={2}
        py={1.5}
        role="log"
        aria-live="polite"
        aria-label={t('Conversation')}
      >
        <Stack component="ul" spacing={1.5}>
          {messages.map((message) => (
            <MessageItem key={message.id} message={message} />
          ))}
          {sending && (
            <Box component="li" data-role="typing">
              <Bubble role="status" aria-label={t('The assistant is typing')}>
                <Typography variant="body2" color="textSecondary">
                  {t('Thinking...')}
                </Typography>
              </Bubble>
            </Box>
          )}
          {!sending && error && (
            <Box component="li" data-role="error">
              <Bubble role="alert" error>
                <Stack spacing={1} alignItems="flex-start">
                  <Typography variant="body2">{error}</Typography>
                  {canRetry && (
                    <Button size="small" variant="outlined" onClick={retry}>
                      {t('Retry')}
                    </Button>
                  )}
                </Stack>
              </Bubble>
            </Box>
          )}
        </Stack>
        <div ref={endRef} />
      </Box>

      {draft.length > max * 0.9 && (
        <Typography
          variant="caption"
          color={overLimit ? 'error' : 'textSecondary'}
          textAlign="right"
          px={2}
          aria-live="polite"
        >
          {draft.length} / {max}
        </Typography>
      )}

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={1}
        px={2}
        py={1.5}
        borderTop={1}
        borderColor="divider"
      >
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('Ask a question...')}
          multiline
          maxRows={5}
          fullWidth
          size="small"
          disabled={sending}
          slotProps={{
            htmlInput: { maxLength: max, 'aria-label': t('Your message') },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!canSend}
          aria-label={t('Send message')}
        >
          <Fa name="paper-plane" size="sm" />
        </IconButton>
      </Stack>
    </Stack>
  )

  if (downSm) {
    return (
      <Dialog fullScreen open={open} onClose={onClose}>
        {content}
      </Dialog>
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(1),
          },
        },
      }}
    >
      {content}
    </Popover>
  )
}

const MessageItem: FC<{ message: SupportChatMessage }> = ({ message }) => {
  const { t } = useTranslation()
  const isUser = message.role === 'user'
  const citations = uniqueCitations(message.citations)

  return (
    <Box
      component="li"
      data-role={message.role}
      display="flex"
      flexDirection="column"
      alignItems={isUser ? 'flex-end' : 'flex-start'}
    >
      <Bubble user={isUser} aria-label={isUser ? t('You') : t('Assistant')}>
        {isUser ? (
          <Typography variant="body2" whiteSpace="pre-wrap">
            {message.content}
          </Typography>
        ) : (
          <Typography variant="body2" component="div">
            {renderMarkdown(message.content)}
          </Typography>
        )}
      </Bubble>
      {!isUser && citations.length > 0 && (
        <Typography variant="caption" color="textSecondary" mt={0.5}>
          {t('Sources')}:{' '}
          {citations.map((citation, index) => (
            <span key={citation.url}>
              {index > 0 && ', '}
              <a href={citation.url} target="_blank" rel="noopener noreferrer">
                {citationLabel(citation)}
              </a>
            </span>
          ))}
        </Typography>
      )}
    </Box>
  )
}

const Bubble: FC<BubbleProps> = ({ user, error, children, ...props }) => {
  const theme = useTheme()
  return (
    <Box
      {...props}
      sx={{
        maxInlineSize: '85%',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        overflowWrap: 'anywhere',
        bgcolor: error
          ? theme.palette.error.light
          : user
          ? 'var(--theme-secondary)'
          : theme.palette.grey[100],
        '& p, & ol, & ul': { m: 0 },
        '& p + *, & ol + *, & ul + *': { mt: 1 },
        '& ol, & ul': { pl: 2.5, listStyle: 'revert' },
        '& a': { color: 'inherit', textDecoration: 'underline' },
      }}
    >
      {children}
    </Box>
  )
}

const citationLabel = (citation: Dto.SupportChatCitation) => {
  if (citation.title) {
    return citation.title
  }
  try {
    return new URL(citation.url).hostname
  } catch {
    return citation.url
  }
}

/** Drops malformed citations and repeats of the same URL. */
const uniqueCitations = (citations: Dto.SupportChatCitation[] | undefined) => {
  const seen = new Set<string>()
  return (citations || []).filter((citation) => {
    if (!citation?.url || seen.has(citation.url)) {
      return false
    }
    seen.add(citation.url)
    return true
  })
}

type SupportChatPanelProps = {
  anchor?: HTMLElement
  onClose: () => void
  chat: SupportChatState
}

type BubbleProps = PropsWithChildren<{
  user?: boolean
  error?: boolean
  role?: string
  'aria-label'?: string
}>

export { SupportChat, SupportChatPanel }
